/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Col, Divider, Form, Input, Popconfirm, Radio, Row, Select, Space, Typography } from 'antd';
import { QuestionCircleOutlined, TeamOutlined } from '@ant-design/icons';
import BotAvatar from '../UI/BotAvatar';
import MultiDropdown from './Fields/MultiDropdown';

// Misc imports
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import { ADDITIONAL_ROLES_ORG, PERMISSIONS_ORG } from '../../constants/main';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useCreatePredefinedResponseMutation, useDeletePredefinedResponseMutation, useUpdatePredefinedResponseMutation } from '../../store/predefinedEntities/predefinedEntitiesApi';
import { useFetchOrganizationPredefinedResponsesQuery } from '../../store/liveChatSettings/liveChatSettingsApi';
import { ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX } from '../../constants/regex';
import useTheme from '../../utils/hooks/useTheme';

// Constant declarations
const ADMIN_PERMISSIONS = [PERMISSIONS_ORG.ADMIN, ADDITIONAL_ROLES_ORG.LIVECHAT_ADMIN];

const PredefinedResponsesForm = ({
  orgFormInstance,
  selectedItem = {},
  onTriggerResponse = () => {},
  setSelectedResponse,
  setIsModalOpen,
  orgLevelForm: isOrgLevelForm = false,
  initialValues = {}
}) => {
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [chatForm] = Form.useForm();
  const form = isOrgLevelForm ? orgFormInstance : chatForm;
  const { formProps } = useTheme();

  const { currentData: activeConversationInfo } = getCurrentQueryState('fetchActiveConversation', { orgId, conversationId: searchParams.get('conversation') });
  const { data: predefinedResponses } = getCurrentQueryState('fetchPredefinedResponses', { orgId, urlParams: { tenantScope: activeConversationInfo?.tenantId } });
  const { data: organizationPredefinedResponses } = useFetchOrganizationPredefinedResponsesQuery({ orgId }, { skip: !orgId });
  const { data: bots, isLoading: isLoadingBots } = getCurrentQueryState('fetchBots', { orgId });
  const [deletePredefinedResponse] = useDeletePredefinedResponseMutation();
  const [createPredefinedResponse, { isLoading: isCreatingPredefinedResponse }] = useCreatePredefinedResponseMutation({ fixedCacheKey: 'createPredefinedResponse' });
  const [updatePredefinedResponse, { isLoading: isUpdatingPredefinedResponse }] = useUpdatePredefinedResponseMutation({ fixedCacheKey: 'updatePredefinedResponse' });

  const userPermissions = useUserPermissions(orgId, activeConversationInfo?.tenantId);
  const hasAdminRights = ADMIN_PERMISSIONS.some(permission => userPermissions.includes(permission));

  const onDeleteResponse = async () => {
    if (selectedItem) {
      deletePredefinedResponse({ orgId, predefinedResponseId: selectedItem.id });
    }
  };

  const onSubmitResponse = async (values) => {
    let valuesToSubmit = {
      text: values.responseMessage,
      name: values.responseName,
      categories: values.categories,
      organizationWide: values.organizationWide,
      tenantIds: values.tenantIds
    };

    let isSaveSuccessful;
    if (selectedItem?.id) {
      isSaveSuccessful = await updatePredefinedResponse({ orgId, predefinedResponseId: selectedItem.id, data: valuesToSubmit, isOrgLevel: isOrgLevelForm }).unwrap();
    } else {
      isSaveSuccessful = await createPredefinedResponse({ orgId, data: valuesToSubmit, isOrgLevel: isOrgLevelForm }).unwrap();
    }

    if (isSaveSuccessful) {
      if (isOrgLevelForm) {
        if (!selectedItem?.id) {
          setIsModalOpen(false);
          form.resetFields();
        }
      } else {
        setSelectedResponse(null);
      }
    }
  };

  return (
    <Form
      form={form}
      className={isOrgLevelForm ? '' : 'bg-layout border-radius-base px-4 pb-4 my-4'}
      layout="vertical"
      onFinish={onSubmitResponse}
      initialValues={initialValues}
    >
      <Row>
        <Col span={24}>
          {
            !isOrgLevelForm ? (
              <Fragment>
                <Row justify="space-between" align="middle">
                  <Col>
                    <Typography.Paragraph
                      className="fs-lg my-1"
                    >
                      {selectedItem.id ? t('Edit canned response') : t('Create canned response')}
                    </Typography.Paragraph>
                  </Col>

                  <Col>
                    <Button
                      hidden={!selectedItem?.id}
                      type="link"
                      className="fs-lg"
                      onClick={() => onTriggerResponse(selectedItem)}
                    >
                      {t('Use it')}
                    </Button>
                  </Col>
                </Row>

                <Divider className="mt-0 mb-2"/>
              </Fragment>
            ) : null
          }

          <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text>
                {t('Response Details')}
              </Typography.Text>
              <Typography.Paragraph className="fs-sm" type="secondary">
                {t('Customize your canned responses: set the button title and compose the message for customers.')}
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              <Form.Item
                name="responseName"
                label={t('Button Title')}
                tooltip={{
                  icon: <QuestionCircleOutlined />,
                  title: t('Enter a short title for the canned response button.')
                }}
                rules={[
                  {
                    required: true,
                    message: t('Enter a name for this response')
                  }
                ]}
              >
                <Input
                  placeholder={t('Response name')}
                  disabled={selectedItem.organizationWide && !hasAdminRights}
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="responseMessage"
                className="mb-0"
                label={t('Message')}
                tooltip={{
                  icon: <QuestionCircleOutlined />,
                  title: t('Enter the canned response message. This is the message that will be sent to the user.')
                }}
                rules={[
                  {
                    required: true,
                    message: t('Enter a message for this response')
                  }
                ]}
              >
                <Input.TextArea
                  placeholder={t('Response message')}
                  size="large"
                  disabled={selectedItem.organizationWide && !hasAdminRights}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text>
                {t('Add Categories')}
              </Typography.Text>
              <Typography.Paragraph className="fs-sm" type="secondary">
                {t('Use tags to organize and categorize your responses efficiently.')}
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              <Form.Item
                className="mb-0"
                name="categories"
                label={t('Categories')}
                labelCol={{ span: 6 }}
                labelAlign="left"
              >
                <Select
                  mode="tags"
                  size="large"
                  tokenSeparators={[',']}
                  placeholder={t('Add a category or a list of categories')}
                  // Options are all categories from all predefined responses
                  options={
                    (activeConversationInfo ? predefinedResponses : organizationPredefinedResponses || [])
                      .map(response => response.categories) // Get all categories
                      .flat() // Flatten the array to a single array
                      .filter((category, index, self) => category && self.indexOf(category) === index) // Remove duplicates (and empty strings)
                      .map(category => ({ label: category, value: category }))
                  }
                  disabled={!hasAdminRights}
                  onInputKeyDown={(e) => {
                    if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX.test(e.key)) {
                      e.preventDefault();
                      return false;
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text>
                {t('Visibility & Availability')}
              </Typography.Text>
              <Typography.Paragraph className="fs-sm" type="secondary">
                {t('Control who can view & use this response.')}
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>

              <Form.Item
                name="organizationWide"
                label={<Typography.Text className="fw-500">{t('Public to Users')}</Typography.Text>}
              >
                <Radio.Group
                  disabled={!hasAdminRights}
                  className="d-flex flex-column"
                >
                  <Radio className="mb-2" value={false}>{t('Only owner')}</Radio>
                  <Radio className="mb-2" value={true}>{t('All users')} <TeamOutlined/></Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name="tenantIds"
                label={<Typography.Text className="fw-500">{t('Available to agents')}</Typography.Text>}
              >
                {
                  isOrgLevelForm ? (
                    <MultiDropdown
                      size="large"
                      placeholder={t('Select agents')}
                      allowClear
                      loading={isLoadingBots}
                      autoClearSearchValue={false}
                      hideExtraFromDropdown
                      options={(bots?.items || []).map(bot => ({
                        label: <BotAvatar bot={bot} wrapBotName maxNameCharacters={28} className="d-inline-block w-auto" />,
                        value: bot.id,
                        searchValue: bot.displayName
                      }))}
                    />
                  ) : (
                    <MultiDropdown
                      disabled={selectedItem.organizationWide && !hasAdminRights}
                      size="large"
                      placeholder={t('Select agents')}
                      allowClear
                      loading={isLoadingBots}
                      autoClearSearchValue={false}
                      hideExtraFromDropdown
                      options={(bots?.items || []).map(bot => ({
                        label: <BotAvatar bot={bot} wrapBotName maxNameCharacters={28} className="d-inline-block w-auto" />,
                        value: bot.id,
                        searchValue: bot.displayName
                      }))}
                    />
                  )
                }
              </Form.Item>
            </Col>
          </Row>
        </Col>

        {
          !isOrgLevelForm ? (
            <Col span={24}>
              <Space className="mb-2">
                <Button
                  size="large"
                  style={{ width: 140 }}
                  onClick={() => setSelectedResponse(null)}
                >
                  {t('Discard Changes')}
                </Button>

                <Button
                  size="large"
                  htmlType="submit"
                  type="primary"
                  disabled={selectedItem.organizationWide && !hasAdminRights}
                  loading={isUpdatingPredefinedResponse || isCreatingPredefinedResponse}
                  style={{ width: 140 }}
                >
                  {t('Save Changes')}
                </Button>
              </Space>

              {
                (!selectedItem.organizationWide && selectedItem.id) || (selectedItem.organizationWide && hasAdminRights)? (
                  <Row justify="center">
                    <Popconfirm
                      title="Delete this response?"
                      okText="Confirm"
                      onConfirm={onDeleteResponse}
                    >
                      <Button
                        size="small"
                        className="fs-sm"
                        type="link"
                      >
                        {t('Delete Response')}
                      </Button>
                    </Popconfirm>
                  </Row>
                ) : null
              }
            </Col>
          ) : null
        }
      </Row>
    </Form>
  );
};

PredefinedResponsesForm.propTypes = {
  orgFormInstance: PropTypes.object,
  selectedItem: PropTypes.object,
  setSelectedResponse: PropTypes.func,
  setIsModalOpen: PropTypes.func,
  hasAdminRights: PropTypes.bool,
  orgLevelForm: PropTypes.bool,
  onTriggerResponse: PropTypes.func,
  initialValues: PropTypes.object
};

export default PredefinedResponsesForm;
